import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Bar, BarChart, CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Bot, Camera, Loader2, Search, Sparkles, WandSparkles } from 'lucide-react';
import { PageHeader } from '@/components/shared/page';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox, Select, Switch, Textarea } from '@/components/ui/misc';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { PageLoader } from '@/components/ui/loader';
import { useUserOptions } from '@/hooks/use-user-options';
import { ApiRequestError, apiClient, apiGet, apiPost } from '@/lib/api';
import { useT } from '@/lib/i18n';
import { cn } from '@/lib/utils';
import { useAiStatus } from './ai';

const failed = (error: unknown, fallback: string) => toast.error(error instanceof ApiRequestError ? error.message : fallback);

function AiOff() {
  const t = useT();
  return (
    <div className="rounded-lg border border-dashed bg-card p-6 text-center text-sm text-muted-foreground">
      <Sparkles className="mx-auto mb-2 h-6 w-6 text-primary" />
      {t('The AI assistant is not switched on for this workspace yet. It starts working as soon as the Anthropic API key is added on the server.')}
    </div>
  );
}

// ══ AI analyst ═══════════════════════════════════════════════════════════════

type Answer = {
  question: string;
  title: string;
  chart: 'bar' | 'line' | 'number' | 'table';
  measureLabel: string;
  money: boolean;
  rows: { label: string; value: number }[];
  total: number;
};

const EXAMPLES = [
  'How many leads did we get each month this year?',
  'Total value of deals won in the last 90 days by owner',
  'Open deals by stage',
  'Leads by source in the last 30 days',
  'Open tickets by priority',
  'Outstanding invoice amount by status',
];

/** Reporting → AI analyst (Kommo AI analyst, HubSpot Data Agent). */
export function AnalystPage() {
  const t = useT();
  const { data: status } = useAiStatus();
  const [question, setQuestion] = useState('');
  const [answers, setAnswers] = useState<Answer[]>([]);
  const ask = useMutation({
    mutationFn: (q: string) => apiPost<Answer>('/ai/analyst', { question: q }),
    onSuccess: (answer) => setAnswers((current) => [answer, ...current].slice(0, 10)),
    onError: (error) => failed(error, 'Could not answer that'),
  });
  const submit = (q: string) => {
    if (!q.trim()) return;
    setQuestion(q);
    ask.mutate(q.trim());
  };
  const format = (answer: Answer, value: number) => (answer.money ? `AED ${value.toLocaleString('en-AE', { maximumFractionDigits: 2 })}` : value.toLocaleString('en-AE'));
  return (
    <div>
      <PageHeader title="AI analyst" description="Ask a question about your pipeline, leads, tickets or invoices in plain English or Arabic. Answers come straight from your data, with the role permissions you already have." />
      {status && !status.enabled ? (
        <AiOff />
      ) : (
        <>
          <form
            className="flex gap-2 rounded-xl border bg-card p-2"
            onSubmit={(event) => {
              event.preventDefault();
              submit(question);
            }}
          >
            <Search className="ms-2 mt-2.5 h-5 w-5 shrink-0 text-muted-foreground" />
            <input
              value={question}
              onChange={(event) => setQuestion(event.target.value)}
              placeholder={t('e.g. Total value of deals won this quarter by owner')}
              className="h-10 min-w-0 flex-1 bg-transparent text-[15px] outline-none"
            />
            <Button type="submit" disabled={ask.isPending || !question.trim()}>
              {ask.isPending ? <Loader2 className="animate-spin" /> : <Sparkles />} {t('Ask')}
            </Button>
          </form>
          <div className="mt-3 flex flex-wrap gap-1.5">
            {EXAMPLES.map((example) => (
              <button key={example} type="button" onClick={() => submit(example)} className="rounded-full border bg-card px-3 py-1 text-xs font-semibold text-muted-foreground hover:border-primary hover:text-primary">
                {t(example)}
              </button>
            ))}
          </div>
          <div className="mt-6 space-y-4">
            {answers.map((answer, index) => (
              <div key={`${answer.question}-${index}`} className="rounded-xl border bg-card p-5">
                <p className="text-xs font-semibold text-muted-foreground">{answer.question}</p>
                <h3 className="mt-1 text-lg font-bold">{answer.title}</h3>
                {answer.chart === 'number' || answer.rows.length === 1 ? (
                  <p className="mt-3 text-4xl font-bold">{format(answer, answer.rows[0]?.value ?? 0)}</p>
                ) : answer.chart === 'table' ? (
                  <AnswerTable answer={answer} format={format} />
                ) : (
                  <>
                    <div className="mt-4 h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        {answer.chart === 'line' ? (
                          <LineChart data={answer.rows}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                            <YAxis tick={{ fontSize: 11 }} width={60} />
                            <Tooltip formatter={(value: number) => format(answer, value)} />
                            <Line dataKey="value" stroke="var(--chart-1)" strokeWidth={2.5} dot={{ r: 3 }} />
                          </LineChart>
                        ) : (
                          <BarChart data={answer.rows}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="label" tick={{ fontSize: 11 }} interval={0} />
                            <YAxis tick={{ fontSize: 11 }} width={60} />
                            <Tooltip formatter={(value: number) => format(answer, value)} />
                            <Bar dataKey="value" fill="var(--chart-1)" radius={[4, 4, 0, 0]} />
                          </BarChart>
                        )}
                      </ResponsiveContainer>
                    </div>
                    <AnswerTable answer={answer} format={format} />
                  </>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function AnswerTable({ answer, format }: { answer: Answer; format: (answer: Answer, value: number) => string }) {
  const t = useT();
  return (
    <table className="mt-4 w-full text-sm">
      <tbody>
        {answer.rows.map((row) => (
          <tr key={row.label} className="border-t">
            <td className="py-1.5">{row.label}</td>
            <td className="tabular py-1.5 text-end font-semibold">{format(answer, row.value)}</td>
          </tr>
        ))}
        <tr className="border-t font-bold">
          <td className="py-1.5">{t('Total')}</td>
          <td className="tabular py-1.5 text-end">{format(answer, answer.total)}</td>
        </tr>
      </tbody>
    </table>
  );
}

// ══ AI agent settings ════════════════════════════════════════════════════════

type Agent = { isActive: boolean; name: string; instructions: string | null; mode: 'UNASSIGNED' | 'ALWAYS'; channelIds: string[]; handoffUserId: string | null; maxReplies: number };

/** Conversations → AI agent: answers chats from the knowledge base. */
export function AiAgentPage() {
  const t = useT();
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['ai', 'agent'], queryFn: () => apiGet<{ aiEnabled: boolean; agent: Agent }>('/ai/agent') });
  const { data: channels = [] } = useQuery({ queryKey: ['inbox', 'channels'], queryFn: () => apiGet<{ id: string; name: string; kind: string }[]>('/inbox/channels') });
  const { data: users = [] } = useUserOptions();
  const [form, setForm] = useState<Agent | null>(null);
  const agent = form ?? data?.agent ?? null;
  const save = useMutation({
    mutationFn: () => apiClient.put('/ai/agent', agent),
    onSuccess: () => {
      toast.success(t('AI agent saved'));
      setForm(null);
      void queryClient.invalidateQueries({ queryKey: ['ai', 'agent'] });
    },
    onError: (error) => failed(error, 'Could not save'),
  });
  if (isLoading || !agent) return <PageLoader />;
  const set = (patch: Partial<Agent>) => setForm({ ...agent, ...patch });
  return (
    <div className="max-w-3xl">
      <PageHeader title="AI agent" description="Answers customers on chat, day and night, from your knowledge base and your instructions — and hands the chat to a person when it should." />
      {!data?.aiEnabled && <div className="mb-4"><AiOff /></div>}
      <div className="space-y-4 rounded-xl border bg-card p-5">
        <label className="flex items-center justify-between gap-3">
          <span>
            <span className="flex items-center gap-2 font-bold">
              <Bot className="h-5 w-5 text-primary" /> {t('AI agent is on')}
            </span>
            <span className="text-sm text-muted-foreground">{t('Salesbots still answer first where they are set up.')}</span>
          </span>
          <Switch checked={agent.isActive} onCheckedChange={(value) => set({ isActive: value })} />
        </label>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label>{t('Name customers see')}</Label>
            <Input value={agent.name} onChange={(event) => set({ name: event.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label>{t('When it answers')}</Label>
            <Select value={agent.mode} onChange={(event) => set({ mode: event.target.value as Agent['mode'] })}>
              <option value="UNASSIGNED">{t('Only chats nobody has picked up')}</option>
              <option value="ALWAYS">{t('Every chat, until it hands over')}</option>
            </Select>
          </div>
        </div>
        <div className="space-y-1.5">
          <Label>{t('Instructions')}</Label>
          <Textarea
            rows={7}
            value={agent.instructions ?? ''}
            onChange={(event) => set({ instructions: event.target.value })}
            placeholder={t('What we sell, opening hours, prices you are happy to share, what never to promise, how to greet…')}
          />
          <p className="text-xs text-muted-foreground">{t('It also reads your published help-centre articles.')}</p>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label>{t('Hand chats over to')}</Label>
            <Select value={agent.handoffUserId ?? ''} onChange={(event) => set({ handoffUserId: event.target.value || null })}>
              <option value="">{t("The channel's default person")}</option>
              {users.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.name}
                </option>
              ))}
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>{t('Most replies before handing over')}</Label>
            <Input type="number" min={1} max={30} value={agent.maxReplies} onChange={(event) => set({ maxReplies: Number(event.target.value) || 6 })} />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label>{t('Channels')}</Label>
          <div className="grid gap-1.5 sm:grid-cols-2">
            {channels.map((channel) => (
              <label key={channel.id} className="flex items-center gap-2 rounded border px-2.5 py-1.5 text-sm">
                <Checkbox
                  checked={agent.channelIds.length === 0 || agent.channelIds.includes(channel.id)}
                  onCheckedChange={(checked) => {
                    const all = agent.channelIds.length === 0 ? channels.map((item) => item.id) : agent.channelIds;
                    const next = checked ? [...new Set([...all, channel.id])] : all.filter((id) => id !== channel.id);
                    set({ channelIds: next.length === channels.length ? [] : next });
                  }}
                />
                {channel.name} <span className="text-xs text-muted-foreground">{channel.kind.toLowerCase()}</span>
              </label>
            ))}
            {channels.length === 0 && <p className="text-sm text-muted-foreground">{t('Connect a channel in the inbox first.')}</p>}
          </div>
        </div>
        <div className="flex justify-end">
          <Button onClick={() => save.mutate()} disabled={save.isPending || !form}>
            {t('Save')}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ══ Business card scanning ═══════════════════════════════════════════════════

/** "Scan card" on the contacts list: a photo becomes a contact. */
export function ScanCardButton() {
  const t = useT();
  const { data: status } = useAiStatus();
  const input = useRef<HTMLInputElement>(null);
  const [fields, setFields] = useState<Record<string, string> | null>(null);
  const scan = useMutation({
    mutationFn: async (file: File) => {
      if (file.size > 5 * 1024 * 1024) throw new Error('Use a photo under 5 MB');
      const data = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '');
        reader.onerror = () => reject(new Error('Could not read the photo'));
        reader.readAsDataURL(file);
      });
      return apiPost<Record<string, string>>('/ai/card', { image: data, mediaType: file.type || 'image/jpeg' });
    },
    onSuccess: setFields,
    onError: (error) => failed(error, error instanceof Error ? error.message : 'Could not read the card'),
  });
  if (!status?.enabled) return null;
  return (
    <>
      <input
        ref={input}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) scan.mutate(file);
          event.target.value = '';
        }}
      />
      <Button variant="outline" onClick={() => input.current?.click()} disabled={scan.isPending}>
        {scan.isPending ? <Loader2 className="animate-spin" /> : <Camera />} {t('Scan card')}
      </Button>
      {fields && <CardDialog initial={fields} onClose={() => setFields(null)} />}
    </>
  );
}

function CardDialog({ initial, onClose }: { initial: Record<string, string>; onClose: () => void }) {
  const t = useT();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    firstName: initial.firstName ?? '',
    lastName: initial.lastName ?? '',
    designation: initial.designation ?? '',
    email: initial.email ?? '',
    phone: initial.phone ?? '',
    whatsapp: initial.whatsapp ?? '',
    linkedin: initial.linkedin ?? '',
    companyName: initial.companyName ?? '',
    website: initial.website ?? '',
  });
  const create = useMutation({
    mutationFn: async () => {
      let companyId: string | undefined;
      if (form.companyName.trim()) {
        const options = await apiGet<{ id: string; name: string }[]>('/companies/options', { params: { q: form.companyName.trim() } }).catch(() => []);
        const match = options.find((option) => option.name.toLowerCase() === form.companyName.trim().toLowerCase());
        companyId =
          match?.id ??
          (await apiPost<{ id: string }>('/companies', { name: form.companyName.trim(), ...(form.website ? { website: /^https?:\/\//.test(form.website) ? form.website : `https://${form.website}` } : {}) })).id;
      }
      const { companyName: _c, website: _w, ...contact } = form;
      return apiPost<{ id: string }>('/contacts', { ...Object.fromEntries(Object.entries(contact).filter(([, value]) => value)), companyId });
    },
    onSuccess: (contact) => {
      void queryClient.invalidateQueries({ queryKey: ['contacts'] });
      toast.success(t('Contact created from the card'));
      onClose();
      navigate(`/crm/contacts/${contact.id}`);
    },
    onError: (error) => failed(error, 'Could not create the contact'),
  });
  const field = (key: keyof typeof form, label: string) => (
    <div className="space-y-1">
      <Label className="text-xs">{t(label)}</Label>
      <Input className="h-8" value={form[key]} onChange={(event) => setForm({ ...form, [key]: event.target.value })} />
    </div>
  );
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <WandSparkles className="h-5 w-5 text-primary" /> {t('Read from the card')}
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          {field('firstName', 'First name')}
          {field('lastName', 'Last name')}
          {field('designation', 'Job title')}
          {field('companyName', 'Company')}
          {field('email', 'Email')}
          {field('phone', 'Phone')}
          {field('whatsapp', 'WhatsApp')}
          {field('website', 'Website')}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('Cancel')}
          </Button>
          <Button onClick={() => create.mutate()} disabled={!form.firstName.trim() || create.isPending}>
            {t('Create contact')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ══ Company research ═════════════════════════════════════════════════════════

export function ResearchCompanyButton({ companyId }: { companyId: string }) {
  const t = useT();
  const queryClient = useQueryClient();
  const { data: status } = useAiStatus();
  const [result, setResult] = useState<{ description?: string; talkingPoints?: string[]; filled: string[] } | null>(null);
  const research = useMutation({
    mutationFn: () => apiPost<{ description?: string; talkingPoints?: string[]; filled: string[] }>('/ai/enrich', { companyId }),
    onSuccess: (data) => {
      setResult(data);
      void queryClient.invalidateQueries({ queryKey: ['companies'] });
      void queryClient.invalidateQueries({ queryKey: ['timeline'] });
    },
    onError: (error) => failed(error, 'Could not research the company'),
  });
  if (!status?.enabled) return null;
  return (
    <>
      <Button variant="outline" onClick={() => research.mutate()} disabled={research.isPending}>
        {research.isPending ? <Loader2 className="animate-spin" /> : <Sparkles />} {t('Research')}
      </Button>
      {result && (
        <Dialog open onOpenChange={(open) => !open && setResult(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{t('AI research')}</DialogTitle>
            </DialogHeader>
            {result.description && <p className="text-sm">{result.description}</p>}
            {result.talkingPoints?.length ? (
              <ul className="list-disc space-y-1 ps-5 text-sm">
                {result.talkingPoints.map((point) => (
                  <li key={point}>{point}</li>
                ))}
              </ul>
            ) : null}
            <p className={cn('text-xs text-muted-foreground')}>
              {result.filled.length ? t('Filled in: {fields}.', { fields: result.filled.join(', ') }) : t('Nothing on the record was empty to fill.')} {t('Saved as a note on the company.')}
            </p>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
